import { apiClient } from './apiClient';
import type { FeedItem } from '../types/feedItem';
import { Logger } from '../utils/logger';

interface BackendFeedItemEntity {
  name: string;
  type: string;
  role?: string;
  context?: string;
}

interface BackendFeedItemViewpoint {
  author?: string;
  position?: string;
  citation?: string;
  justifications?: string[];
  confidence?: number;
}

interface BackendFeedItemEditorialAngle {
  type?: string;
  description?: string;
  target_audience?: string;
  hook_suggestion?: string;
}

interface BackendFeedItemVerifiedFact {
  statement?: string;
  source_context?: string;
  confidence?: number;
}

interface BackendFeedItem {
  id: string;
  source_id?: string;
  title?: string;
  link?: string;
  url?: string;
  description?: string;
  content?: string;
  author?: string;
  published_at?: string;
  fetched_at?: string;
  thumbnail?: string;
  content_type?: string;
  language?: string;
  ai_analyzed?: boolean;
  executive_summary?: string;
  synthetic_summary?: string;
  keywords?: string[];
  quality_score?: number;
  entities?: BackendFeedItemEntity[];
  viewpoints?: BackendFeedItemViewpoint[];
  editorial_angles?: BackendFeedItemEditorialAngle[];
  verified_facts?: BackendFeedItemVerifiedFact[];
  risks_guardrails?: unknown;
}

interface BackendFeedItemsResponse {
  items?: BackendFeedItem[];
  // V4-FRONT-ANY-USAGE 2026-05-28 — backend may include total for pagination UIs
  total?: number;
}

interface BackendItemsCountResponse {
  items_count?: number;
}

/**
 * Maps a backend feed item (snake_case) to frontend FeedItem (camelCase).
 */
function mapBackendFeedItemToFrontend(data: BackendFeedItem): FeedItem {
  return {
    id: data.id,
    sourceId: data.source_id ?? '',
    title: data.title || '',
    link: data.link || data.url,
    url: data.url || data.link,
    description: data.description,
    content: data.content,
    author: data.author,
    publishedAt: data.published_at ? new Date(data.published_at) : undefined,
    fetchedAt: data.fetched_at ? new Date(data.fetched_at) : undefined,
    thumbnail: data.thumbnail,
    contentType: data.content_type,
    language: data.language,

    // AI analysis
    aiAnalyzed: data.ai_analyzed ?? false,
    executiveSummary: data.executive_summary,
    syntheticSummary: data.synthetic_summary ? [data.synthetic_summary] : undefined,
    keywords: data.keywords,
    qualityScore: data.quality_score,
    entities: data.entities?.map((e) => ({
      name: e.name,
      type: e.type,
      role: e.role,
      context: e.context,
    })),
    viewpoints: data.viewpoints?.map((v) => ({
      author: v.author,
      position: v.position,
      citation: v.citation,
      justifications: v.justifications,
      confidence: v.confidence,
    })),
    editorialAngles: data.editorial_angles?.map((a) => ({
      type: a.type,
      description: a.description,
      targetAudience: a.target_audience,
      hookSuggestion: a.hook_suggestion,
    })),
    verifiedFacts: data.verified_facts?.map((f) => ({
      statement: f.statement,
      sourceContext: f.source_context,
      confidence: f.confidence,
    })),
    risksGuardrails: Array.isArray(data.risks_guardrails) ? (data.risks_guardrails as string[]) : undefined,
  };
}

export interface FeedItemsPage {
  items: FeedItem[];
  total: number;
}

export interface FeedItemsOptions {
  limit?: number;
  from?: number;
  minScore?: number;
  fromDate?: string;
}

export const feedItemsService = {
  /**
   * Get feed items for a given source ID with pagination and filtering.
   */
  getBySourceId: async (sourceId: string, options: FeedItemsOptions = {}): Promise<FeedItemsPage> => {
    const { limit = 20, from = 0, minScore, fromDate } = options;
    Logger.debug('[FeedItemsService] Fetching feed items by source', { sourceId, limit, from });
    const params: Record<string, unknown> = { limit, from };
    if (minScore && minScore > 0) params.min_score = minScore;
    if (fromDate) params.from_date = fromDate;
    const response = await apiClient.get<BackendFeedItemsResponse>(`/ingestion/feed-items/${sourceId}`, { params });
    const items = (response.data?.items || []).map(mapBackendFeedItemToFrontend);
    // V4-FRONT-ANY-USAGE 2026-05-28
    const total: number = response.data?.total ?? items.length;
    Logger.info('[FeedItemsService] Feed items fetched by source', { sourceId, count: items.length, total });
    return { items, total };
  },

  /**
   * Get feed items associated with a watch topic (web search results) with pagination and filtering.
   */
  getByTopicId: async (topicId: string, options: FeedItemsOptions = {}): Promise<FeedItemsPage> => {
    const { limit = 20, from = 0, minScore, fromDate } = options;
    Logger.debug('[FeedItemsService] Fetching feed items by topic', { topicId, limit, from });
    const params: Record<string, unknown> = { limit, from };
    if (minScore && minScore > 0) params.min_score = minScore;
    if (fromDate) params.from_date = fromDate;
    const response = await apiClient.get<BackendFeedItemsResponse>(`/watch/${topicId}/items`, { params });
    const items = (response.data?.items || []).map(mapBackendFeedItemToFrontend);
    // V4-FRONT-ANY-USAGE 2026-05-28
    const total: number = response.data?.total ?? items.length;
    Logger.info('[FeedItemsService] Feed items fetched by topic', { topicId, count: items.length, total });
    return { items, total };
  },

  /**
   * Get the count of feed items for a watch topic.
   */
  getTopicItemsCount: async (topicId: string): Promise<number> => {
    Logger.debug('[FeedItemsService] Fetching feed items count by topic', { topicId });
    const response = await apiClient.get<BackendItemsCountResponse>(`/watch/${topicId}/items-count`);
    const itemsCount = response.data?.items_count ?? 0;
    Logger.info('[FeedItemsService] Feed items count fetched by topic', { topicId, count: itemsCount });
    return itemsCount;
  },
};
